/**
 * Public dashboard view (no auth).
 *
 * Hit at `/public/dashboard/:token` — fetches the publish endpoint and renders
 * the dashboard's indicators using the same chart components as the
 * authenticated reports page. Pre-resolved chart payloads are pushed into
 * `IndicatorDataContext` so the chart components don't try to call the
 * authenticated `/indicators/{id}/data/` endpoint.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, BarChart3 } from 'lucide-react';
import api from '../lib/api/client';
import {
  IndicatorDataContext,
  type IndicatorDataResponse,
} from './report/charts/useIndicatorData';
import { CHART_TYPE_ICONS } from './report/constants';
import { KPIIndicator } from './report/charts/KPIIndicator';
import { BarChartComponent } from './report/charts/BarChartComponent';
import { LineChartComponent } from './report/charts/LineChartComponent';
import { PieChartComponent } from './report/charts/PieChartComponent';
import { SummaryComponent } from './report/charts/SummaryComponent';
import { GridComponent } from './report/charts/GridComponent';
import { HeatmapComponent } from './report/charts/HeatmapComponent';
import { WaterfallComponent } from './report/charts/WaterfallComponent';
import { GeolocationComponent } from './report/charts/GeolocationComponent';
import { IndicatorChartComponent } from './report/charts/IndicatorChartComponent';
import { MarkUpComponent } from './report/charts/MarkUpComponent';
import { useFormatters } from '../contexts/PreferencesContext';

interface PublicIndicator {
  id: number;
  name: string;
  chart_type: string;
  hint?: string;
  data: IndicatorDataResponse;
}

interface PublicDashboardPayload {
  dashboard: {
    id: number;
    name: string;
    description: string;
    level: number | null;
  };
  indicators: PublicIndicator[];
  published_at: string;
}

function renderChart(chartType: string, indicatorId: number, height: number) {
  switch (chartType) {
    case 'kpi':
      return <KPIIndicator indicatorId={indicatorId} />;
    case 'indicator':
      return <IndicatorChartComponent indicatorId={indicatorId} />;
    case 'bar':
      return <BarChartComponent indicatorId={indicatorId} height={height} />;
    case 'waterfall':
      return <WaterfallComponent indicatorId={indicatorId} height={height} />;
    case 'line':
      return <LineChartComponent indicatorId={indicatorId} height={height} />;
    case 'pie':
      return <PieChartComponent indicatorId={indicatorId} height={height} />;
    case 'donut':
      return <PieChartComponent isDoughnut indicatorId={indicatorId} height={height} />;
    case 'grid':
      return <GridComponent indicatorId={indicatorId} maxHeight={height + 80} />;
    case 'summary':
      return <SummaryComponent indicatorId={indicatorId} />;
    case 'heatmap':
      return <HeatmapComponent indicatorId={indicatorId} />;
    case 'geolocation':
      return <GeolocationComponent indicatorId={indicatorId} />;
    case 'markup':
      return <MarkUpComponent indicatorId={indicatorId} />;
    default:
      return <BarChartComponent indicatorId={indicatorId} height={height} />;
  }
}

export default function PublicDashboard() {
  const { token } = useParams<{ token: string }>();
  const [payload, setPayload] = useState<PublicDashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const { formatDateTime } = useFormatters();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<PublicDashboardPayload>(`/reporting/publish/${token}/`)
      .then((res) => {
        if (!cancelled) setPayload(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError('This link is invalid or has been revoked.');
        } else if (status === 410) {
          setError('This link has been revoked or expired.');
        } else {
          setError('Could not load this dashboard.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Build the indicator-id → resolved-payload map that the chart components
  // read via IndicatorDataContext. The publish endpoint already ran the
  // resolver, so we hand the payloads through directly instead of re-fetching.
  const presetMap = useMemo<Record<number, IndicatorDataResponse>>(() => {
    if (!payload) return {};
    const out: Record<number, IndicatorDataResponse> = {};
    for (const ind of payload.indicators) {
      if (ind?.data && typeof ind.id === 'number') {
        out[ind.id] = ind.data;
      }
    }
    return out;
  }, [payload]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !payload) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Unavailable</CardTitle>
            <CardDescription>{error || 'No data.'}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const { dashboard, indicators, published_at } = payload;

  return (
    <IndicatorDataContext.Provider value={presetMap}>
      <div className="min-h-screen bg-muted/30">
        <header className="border-b bg-background">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <h1 className="text-xl font-semibold">{dashboard.name}</h1>
            {dashboard.description && (
              <p className="text-sm text-muted-foreground mt-1">{dashboard.description}</p>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">
              Published {formatDateTime(published_at)} · Public read-only view
            </p>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-6">
          {indicators.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">
              This dashboard has no indicators yet.
            </div>
          ) : (
            (() => {
              const single = indicators.length === 1;
              const two = indicators.length === 2;
              const gridCls = single
                ? 'grid grid-cols-1 gap-4'
                : two
                  ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
                  : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4';
              const chartHeight = single ? 520 : two ? 380 : 280;
              return (
                <div className={gridCls}>
                  {indicators.map((ind) => (
                    <Card key={ind.id} className="h-full">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {CHART_TYPE_ICONS[ind.chart_type] || <BarChart3 className="h-4 w-4" />}
                            <CardTitle className="text-sm font-medium">{ind.name}</CardTitle>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {ind.chart_type}
                          </Badge>
                        </div>
                        {ind.hint && (
                          <CardDescription className="text-xs">{ind.hint}</CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="pt-0">
                        {renderChart(ind.chart_type, ind.id, chartHeight)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()
          )}
        </main>
      </div>
    </IndicatorDataContext.Provider>
  );
}
